import api from '../utils/api';

/**
 * Campaign Service - Handles marketing campaigns and discount codes
 */

export const campaignService = {
  /**
   * Get all campaigns (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getAllCampaigns: async (filters = {}) => {
    try {
      const response = await api.get('/admin/campaigns', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaigns');
    }
  },

  /**
   * Get campaign by ID
   * @param {string} id - Campaign ID
   * @returns {Promise}
   */
  getCampaignById: async (id) => {
    try {
      const response = await api.get(`/admin/campaigns/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign');
    }
  },

  /**
   * Create a new campaign
   * @param {Object} campaignData - Campaign data
   * @returns {Promise}
   */
  createCampaign: async (campaignData) => {
    try {
      const response = await api.post('/admin/campaigns', campaignData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create campaign');
    }
  },

  /**
   * Update a campaign
   * @param {string} id - Campaign ID
   * @param {Object} campaignData - Updated campaign data
   * @returns {Promise}
   */
  updateCampaign: async (id, campaignData) => {
    try {
      const response = await api.put(`/admin/campaigns/${id}`, campaignData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update campaign');
    }
  },

  /**
   * Delete a campaign
   * @param {string} id - Campaign ID
   * @returns {Promise}
   */
  deleteCampaign: async (id) => {
    try {
      const response = await api.delete(`/admin/campaigns/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete campaign');
    }
  },

  /**
   * Get campaign statistics
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getCampaignStats: async (filters = {}) => {
    try {
      const response = await api.get('/admin/campaigns/stats', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign stats');
    }
  },

  /**
   * Get campaign analytics
   * @param {string} id - Campaign ID
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getCampaignAnalytics: async (id, filters = {}) => {
    try {
      const response = await api.get(`/admin/campaigns/${id}/analytics`, { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign analytics');
    }
  },

  /**
   * Export campaigns to CSV
   * @param {string|null} id - Campaign ID (null for all)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  exportCampaigns: async (id = null, filters = {}) => {
    try {
      const response = await api.post('/admin/campaigns/export', filters, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export campaigns');
    }
  },

  /**
   * Validate a campaign code
   * @param {string} code - Campaign code
   * @param {number} orderAmount - Order amount
   * @param {string} userId - User ID (optional)
   * @returns {Promise}
   */
  validateCampaignCode: async (code, orderAmount = 0, userId = null) => {
    try {
      const params = { orderAmount };
      if (userId) params.userId = userId;
      const response = await api.get(`/campaigns/validate/${code}`, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Invalid campaign code');
    }
  },

  /**
   * Apply a campaign code to an order
   * @param {string} code - Campaign code
   * @param {number} orderAmount - Order amount
   * @param {string} orderId - Order ID
   * @returns {Promise}
   */
  applyCampaignCode: async (code, orderAmount, orderId) => {
    try {
      const response = await api.post('/campaigns/apply', { code, orderAmount, orderId });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to apply campaign code');
    }
  }
};

